import { toPng } from 'html-to-image'
import { jsPDF } from 'jspdf'
import { loadBrandLogoPng, type LogoPng } from '@/lib/seatingPlannerLogo'
import type { PlanState, SeatingGuest, SeatingTable } from '@/types/seatingPlanner'

const A4_LANDSCAPE_WIDTH_MM = 297
const A4_LANDSCAPE_HEIGHT_MM = 210
const PAGE_PADDING_MM = 12
const LOGO_WIDTH_MM = 42

export async function exportPlanToPdf(
  state: PlanState,
  canvasEl: HTMLElement,
  filename = 'seating-chart.pdf',
): Promise<void> {
  const [chartDataUrl, logo] = await Promise.all([
    toPng(canvasEl, { pixelRatio: 2, cacheBust: true, backgroundColor: '#ffffff' }),
    loadBrandLogoPng(800).catch(() => null),
  ])

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

  await drawChartPage(pdf, chartDataUrl, state, logo)
  drawGuestListPages(pdf, state, logo)

  pdf.save(filename)
}

async function drawChartPage(
  pdf: jsPDF,
  dataUrl: string,
  state: PlanState,
  logo: LogoPng | null,
): Promise<void> {
  const headerHeight = drawEventHeader(pdf, state, logo)
  const img = await loadImage(dataUrl)
  const availableWidth = A4_LANDSCAPE_WIDTH_MM - PAGE_PADDING_MM * 2
  const availableHeight = A4_LANDSCAPE_HEIGHT_MM - PAGE_PADDING_MM - headerHeight - 4

  const scale = Math.min(availableWidth / img.width, availableHeight / img.height)
  const renderWidth = img.width * scale
  const renderHeight = img.height * scale
  const offsetX = (A4_LANDSCAPE_WIDTH_MM - renderWidth) / 2
  const offsetY = headerHeight + 4 + (availableHeight - renderHeight) / 2

  pdf.addImage(dataUrl, 'PNG', offsetX, offsetY, renderWidth, renderHeight)
}

function drawEventHeader(pdf: jsPDF, state: PlanState, logo: LogoPng | null): number {
  const event = state.event ?? { name: '', date: '', venue: '' }
  const title = event.name?.trim() || 'Seating Chart'
  const subtitleParts: string[] = []
  if (event.date?.trim()) subtitleParts.push(formatPdfDate(event.date))
  if (event.venue?.trim()) subtitleParts.push(event.venue.trim())
  const subtitle = subtitleParts.join(' · ')

  let logoBottomY = PAGE_PADDING_MM
  if (logo) {
    const logoHeightMm = LOGO_WIDTH_MM / logo.aspect
    pdf.addImage(logo.dataUrl, 'PNG', PAGE_PADDING_MM, PAGE_PADDING_MM, LOGO_WIDTH_MM, logoHeightMm)
    logoBottomY = PAGE_PADDING_MM + logoHeightMm
  }

  const titleY = logoBottomY + (logo ? 6 : 6)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(18)
  pdf.text(title, PAGE_PADDING_MM, titleY)

  let nextY = titleY + 4

  if (subtitle) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(11)
    pdf.setTextColor(80)
    pdf.text(subtitle, PAGE_PADDING_MM, nextY + 4)
    pdf.setTextColor(0)
    nextY += 6
  }

  return nextY
}

function formatPdfDate(iso: string): string {
  const trimmed = iso.trim()
  // Accept either YYYY-MM-DD or any Date-parseable string.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? new Date(`${trimmed}T00:00:00`)
    : new Date(trimmed)
  if (Number.isNaN(date.getTime())) return trimmed
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function drawGuestListPages(pdf: jsPDF, state: PlanState, logo: LogoPng | null): void {
  const tablesById = new Map(state.tables.map((t) => [t.id, t]))
  const grouped = new Map<string, SeatingGuest[]>()
  for (const t of state.tables) grouped.set(t.id, [])
  const unassigned: SeatingGuest[] = []

  for (const g of state.guests) {
    const assignment = state.assignments[g.id]
    if (assignment && tablesById.has(assignment.tableId)) {
      grouped.get(assignment.tableId)!.push(g)
    } else {
      unassigned.push(g)
    }
  }

  for (const tableId of grouped.keys()) {
    const list = grouped.get(tableId)!
    list.sort((a, b) => {
      const ai = state.assignments[a.id]?.seatIndex ?? 0
      const bi = state.assignments[b.id]?.seatIndex ?? 0
      return ai - bi
    })
  }

  const sortedTables = [...state.tables].sort((a, b) =>
    naturalCompare(a.number || '', b.number || ''),
  )

  pdf.addPage('a4', 'landscape')
  let cursorY = PAGE_PADDING_MM
  if (logo) {
    const logoHeightMm = LOGO_WIDTH_MM / logo.aspect
    pdf.addImage(logo.dataUrl, 'PNG', PAGE_PADDING_MM, PAGE_PADDING_MM, LOGO_WIDTH_MM, logoHeightMm)
    cursorY = PAGE_PADDING_MM + logoHeightMm + 4
  }
  cursorY = drawHeading(pdf, 'Guest list by table', cursorY)

  for (const table of sortedTables) {
    cursorY = ensureRoom(pdf, cursorY, 16)
    cursorY = drawTableHeader(pdf, table, cursorY)
    const guests = grouped.get(table.id) || []
    if (guests.length === 0) {
      cursorY = drawLine(pdf, '— No guests assigned —', cursorY, { italic: true })
    } else {
      for (const g of guests) {
        cursorY = ensureRoom(pdf, cursorY, 6)
        cursorY = drawLine(pdf, formatGuestLine(g), cursorY)
      }
    }
    cursorY += 3
  }

  if (unassigned.length > 0) {
    cursorY = ensureRoom(pdf, cursorY, 16)
    cursorY = drawHeading(pdf, 'Unassigned guests', cursorY, 13)
    for (const g of unassigned) {
      cursorY = ensureRoom(pdf, cursorY, 6)
      cursorY = drawLine(pdf, formatGuestLine(g), cursorY)
    }
  }
}

function drawHeading(pdf: jsPDF, text: string, y: number, size = 16): number {
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(size)
  pdf.text(text, PAGE_PADDING_MM, y + size * 0.35)
  return y + size * 0.6 + 2
}

function drawTableHeader(pdf: jsPDF, table: SeatingTable, y: number): number {
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(12)
  const number = table.number?.trim() || '—'
  const label = table.label?.trim()
  const headline = label ? `Table ${number} — ${label}` : `Table ${number}`
  pdf.text(headline, PAGE_PADDING_MM, y + 4)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(110)
  pdf.text(`Seats: ${table.capacity}`, PAGE_PADDING_MM + 100, y + 4)
  pdf.setTextColor(0)
  return y + 7
}

function drawLine(pdf: jsPDF, text: string, y: number, opts: { italic?: boolean } = {}): number {
  pdf.setFont('helvetica', opts.italic ? 'italic' : 'normal')
  pdf.setFontSize(10)
  pdf.text(text, PAGE_PADDING_MM + 4, y + 4)
  return y + 5.5
}

function ensureRoom(pdf: jsPDF, y: number, needed: number): number {
  const limit = A4_LANDSCAPE_HEIGHT_MM - PAGE_PADDING_MM
  if (y + needed > limit) {
    pdf.addPage('a4', 'landscape')
    return PAGE_PADDING_MM
  }
  return y
}

function formatGuestLine(g: SeatingGuest): string {
  const parts = [g.name]
  if (g.notes) parts.push(`— ${g.notes}`)
  return parts.join(' ')
}

function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image_load_failed'))
    img.src = src
  })
}
